import { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { enqueueSnackbar } from "notistack"
import PropTypes from 'prop-types';
import { Box, Typography, Grid, Tooltip, Button, Menu } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers';
import { useSelector } from '../../../../redux/store';
import Iconify from '../../../../reusable-components/iconify';
import { postSaveRequestedVendorShipDate, removeRequestedVendorShipDate } from '../../../../api/orders/index';
import { fDayMonthDayShort, fDayMonthDy, hoursLeftToDate, fTime } from '../../../../utils/formatTime';
import { ScopeGuard } from '../../../../reusable-components/scopes';
import AwaitButton from '../../../../reusable-components/await-button/AwaitButton';
import { ConfirmDialogModal } from '../../../../reusable-components/confirm-dialog';

const getFirstSchedule = (vendorMessages) => {
  const now = new Date();
  return [...vendorMessages].sort((a, b) => {
    const aHasActiveCutoff = Boolean(a.cutoffEndDate && new Date(a.cutoffEndDate) > now);
    const bHasActiveCutoff = Boolean(b.cutoffEndDate && new Date(b.cutoffEndDate) > now);
    if (aHasActiveCutoff !== bHasActiveCutoff) return aHasActiveCutoff ? -1 : 1;
    const aDate = aHasActiveCutoff ? a.cutoffEndDate : a.shipDate || a.cutoffEndDate;
    const bDate = bHasActiveCutoff ? b.cutoffEndDate : b.shipDate || b.cutoffEndDate;
    return new Date(aDate) - new Date(bDate);
  })[0];
};

const isCutoffExpired = (cutoffEndDate) => cutoffEndDate && new Date(cutoffEndDate) < new Date();

const getCutoffStatus = (cutoffEndDate) => {
  const time = hoursLeftToDate(cutoffEndDate);
  return isCutoffExpired(cutoffEndDate) ? `Cutoff missed by ${time.replace(' ago', '')}` : `Order within ${time}`;
};

const filterUniqueMessagesByVendorId = (messages) => {
  const uniqueMessages = {};
  messages.forEach((msg) => {
    const { vendorId } = msg;
    if (!uniqueMessages[vendorId]) uniqueMessages[vendorId] = [];
    uniqueMessages[vendorId].push(msg);
  });
  return uniqueMessages;
};

VendorDate.propTypes = {
  item: PropTypes.object,
  allReqDates: PropTypes.array,
  setAllReqDates: PropTypes.func,
  disableEdits: PropTypes.bool,
  vendorDates: PropTypes.array,
};

export default function VendorDate({ item, allReqDates, setAllReqDates, disableEdits = false, vendorDates = [] }) {
  const { id = '' } = useParams();
  const { orderById } = useSelector((state) => state.orders || {});
  const { vendorDateMessages = [], vendors } = orderById[id] || {};
  const { user } = useSelector((state) => state.user);
                              
  const { vendorId } = item;

  const uniqueMessagesLookup = useMemo(() => filterUniqueMessagesByVendorId(vendorDateMessages || []), [vendorDateMessages]);

  const vendorAllowsRequestedShipDate = vendors?.find((vendor) => vendor.id === item.vendorId)?.allowsRequestedShipDate;

  const [selectedDate, setSelectedDate] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openRemoveConfirm, setOpenRemoveConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const vendorMessages = uniqueMessagesLookup[item.vendorId] || [];
  const firstSchedule = getFirstSchedule(vendorMessages);
  const remainingSchedules = vendorMessages.filter((schedule) => (schedule.id !== firstSchedule?.id || !firstSchedule?.cutoffEndDate) && schedule.shipDate);

  const getRequestedDateText = (dateObj) => {
    const userText = vendorDates.find((date) => date.vendorId === dateObj.vendorId)
      ? vendorDates.find((date) => date.vendorId === dateObj.vendorId).requestedBy || ''
      : `${user.firstName} ${user.lastName}`;
    const requestedShipText = `Requested ship date - ${fDayMonthDy(dateObj.date)}`;
    if(!userText) return requestedShipText 
    return `${userText} ${requestedShipText}`;
  }

  useEffect(() => {
    setSelectedDate(allReqDates?.find((date) => date.vendorId === vendorId) || null);
  }, [allReqDates, vendorId]);

  const handleRequestedDateChange = (newDate) => {
    setSelectedDate({ vendorId, date: newDate });
  }

  const handleRequestedDateSave = () => {
    setIsSaving(true);
    return postSaveRequestedVendorShipDate(id, vendorId, selectedDate?.date)
      .then((response) => {
        setAllReqDates((prevDates) => {
          const existingDateIndex = prevDates?.findIndex((dateEntry) => dateEntry.vendorId === vendorId);

          if (existingDateIndex !== -1) {
            return prevDates.map((dateEntry, index) =>
              index === existingDateIndex ? { ...dateEntry, date: selectedDate?.date } : dateEntry
            );
          }
          return [...prevDates, { vendorId, date: selectedDate?.date }];
        });
        enqueueSnackbar(response.data.message || 'Requested ship date saved successfully', { variant: 'success' });
        setAnchorEl(null);
      })
      .catch(() => {
        enqueueSnackbar('Failed to edit requested ship date', { variant: 'error' });
        setAnchorEl(null);
        setSelectedDate(allReqDates?.find((date) => date.vendorId === vendorId) || null);
      })
      .finally(() => setIsSaving(false));
  }

  const handleRequestedDateCalendarClose = () => {
    if (isSaving) return;
    setAnchorEl(null);
    setSelectedDate(allReqDates?.find((date) => date.vendorId === vendorId) || null);
  }

  const handleOpenRemoveConfirm = (e) => {
    e.stopPropagation();
    setOpenRemoveConfirm(true);
  }

  const handleRemoveRequestedShipDate = () => {
    removeRequestedVendorShipDate(id, vendorId)
      .then(() => {
        setAllReqDates((prevDates) => prevDates.filter((dateEntry) => dateEntry.vendorId !== vendorId));
        enqueueSnackbar('Requested ship date removed successfully', { variant: 'success' });
      }).catch(() => {
        enqueueSnackbar('Failed to remove requested ship date', { variant: 'error' });
      })
  }

  const showShipDateEditor = useMemo(() => vendorAllowsRequestedShipDate && !disableEdits, [vendorAllowsRequestedShipDate, disableEdits]);

  return (
    <Box sx={{ pl: 2 }}>
      <Grid container direction="row" justifyContent="flex-start" alignItems="flex-end" sx={{ pl: 1 }}>
        <Iconify icon="ic:outline-local-shipping" color="secondary" sx={{ mr: 0.5, mb: 0.4 }} width={16} height={16} />
        <Tooltip
          title={
            vendorAllowsRequestedShipDate ? (
              ''
            ) : (
              <span style={{ whiteSpace: 'normal' }}>Vendor does not accept ship date requests</span>
            )
          }
          arrow
          slotProps={{
            tooltip: {
              sx: { maxWidth: 150, whiteSpace: 'normal', fontSize: 11 },
            },
          }}
        >
          <Typography variant="subtitle2">Ship Info</Typography>
        </Tooltip>

        <ScopeGuard scopes={['Edit-Order']} allowAdmin>
          <Box
            sx={{
              display: 'flex',
              '&:hover': { cursor: showShipDateEditor ? 'pointer' : 'default' },
              mt: 0.5,
              ml: 1,
            }}
            onClick={(e) => {
              if (showShipDateEditor) setAnchorEl(e.currentTarget);
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                textDecoration: showShipDateEditor ? 'underline' : '',
                color: (theme) => (showShipDateEditor ? theme.palette.secondary.main : theme.palette.grey[700]),
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {showShipDateEditor && (
                <Tooltip title="Request ship date change" arrow>
                  <span>
                    <Iconify
                      icon="material-symbols:edit-outline"
                      sx={{ mr: 0.5 }}
                      width={16}
                      height={16}
                    />
                  </span>
                </Tooltip>
              )}
              {selectedDate && vendorAllowsRequestedShipDate
                ? getRequestedDateText(selectedDate)
                : !vendorAllowsRequestedShipDate && !uniqueMessagesLookup[item.vendorId]?.length
                ? 'None available'
                : ''}
            </Typography>
            {selectedDate && showShipDateEditor && (
              <Tooltip title="Cancel request" arrow>
                <Box
                  component="button"
                  sx={{
                    border: 'none',
                    borderRadius: '50%',
                    background: 'transparent',
                    color: 'secondary.main',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                      background: (theme) => theme.palette.grey[300],
                    },
                  }}
                  onClick={handleOpenRemoveConfirm}
                >
                  <Iconify icon="material-symbols:cancel-outline" width={16} height={16} />
                </Box>
              </Tooltip>
            )}
          </Box>
        </ScopeGuard>
      </Grid>
      {firstSchedule?.cutoffEndDate && (
          <Typography variant="subtitle2" sx={{
            color: (theme) => isCutoffExpired(firstSchedule.cutoffEndDate)
              ? theme.palette.error.main : theme.palette.grey[700],
            pl: 1
          }}>
            1) &nbsp;<span style={{ textDecoration: 'underline' }}>Cutoff:</span>
            &nbsp;{fDayMonthDayShort(firstSchedule.cutoffEndDate)}
            &nbsp;{fTime(firstSchedule.cutoffEndDate)} EST
            <Box sx={{ ml: 2, display: 'block' }}>
              <span style={{ textDecoration: 'underline' }}>Ship Date:</span>
              &nbsp;{fDayMonthDayShort(firstSchedule.shipDate)}
            </Box>
            <Box sx={{ fontWeight: 'bold', ml: 2, display: 'block' }}>
              {getCutoffStatus(firstSchedule.cutoffEndDate)}
            </Box>
          </Typography>
        )}
      {remainingSchedules.map((schedule, index) => (
        <Box key={schedule.id ?? index} sx={{ pl: 1 }}>
          <Typography variant="subtitle2" sx={{ color: (theme) => theme.palette.grey[700] }}>
            {index + (firstSchedule?.cutoffEndDate ? 2 : 1)}) &nbsp;
            <span style={{ textDecoration: 'underline' }}>Ship Date:</span>
            &nbsp;{fDayMonthDayShort(schedule.shipDate)}
          </Typography>
        </Box>
      ))}

      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleRequestedDateCalendarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        MenuListProps={{ sx: { p: 0 } }}
        PaperProps={{ sx: { p: 2 } }}
      >
        <Box>
          <DateCalendar
            value={selectedDate?.date || null}
            onChange={handleRequestedDateChange}
            minDate={new Date()}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button color="primary" onClick={handleRequestedDateCalendarClose} disabled={isSaving}>
              Cancel
            </Button>
            <Tooltip disableHoverListener={!!selectedDate?.date} title={'Please select a date'} arrow placement="top">
              <span>
                <AwaitButton variant="contained" color="primary" onClick={handleRequestedDateSave} disabled={!selectedDate?.date}> Save </AwaitButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Menu>

      <ConfirmDialogModal
        title="Cancel Requested Ship Date"
        content="Are you sure you want to cancel this requested ship date?"
        actionButton="Yes"
        color="error"
        icon="delete-outline"
        action={handleRemoveRequestedShipDate}
        setOpenConfirm={setOpenRemoveConfirm}
        openConfirm={openRemoveConfirm}
      />
    </Box>
  );
}
